'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { EventService } from '@/shared/lib/event-service';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';

type Props = {
    age?: number;
    children?: ReactNode;
} & ModalProps;

type UnderAgeVerificationModalContextValue = {
    onClose: ModalProps['onClose'];
    age?: number;
};

const UnderAgeVerificationModalContext = createContext<UnderAgeVerificationModalContextValue | null>(null);

const useUnderAgeVerificationModalContext = () => {
    const context = useContext(UnderAgeVerificationModalContext);
    if (!context) {
        throw new Error(
            'UnderAgeVerificationModal components must be used within UnderAgeVerificationModalWrapper.',
        );
    }
    return context;
};

export const UnderAgeVerificationModalWrapper = ({ age, children, ...props }: Props) => {
    return (
        <UnderAgeVerificationModalContext.Provider value={{ onClose: props.onClose, age }}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'under-age-verification-modal',
                    'data-testid': 'under-age-verification-modal',
                }}
            >
                {children}
            </Modal>
        </UnderAgeVerificationModalContext.Provider>
    );
};

export const UnderAgeVerificationModalProvider = ({ age, children, ...props }: Props) => {
    return (
        <UnderAgeVerificationModalContext.Provider value={{ onClose: props.onClose, age }}>
            {children}
        </UnderAgeVerificationModalContext.Provider>
    );
};

const UnderAgeVerificationModalTitle = () => {
    const t = useTranslations();
    return <>{t('AGE_VERIFICATION_MODAL.UNDERAGE_TITLE')}</>;
};

const UnderAgeVerificationModalDescription = () => {
    const t = useTranslations();
    const { onClose } = useUnderAgeVerificationModalContext();

    return (
        <>
            {t.rich('AGE_VERIFICATION_MODAL.UNDERAGE_MESSAGE', {
                link: (chunks) => (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onClose();
                            EventService.trigger('account:view-detail');
                        }}
                        style={{ textDecoration: 'underline' }}
                    >
                        {chunks}
                    </a>
                ),
            })}
        </>
    );
};

type ActionButtonProps = ComponentProps<typeof Button>;

const UnderAgeVerificationModalOkayButton = ({ onClick, ...props }: ActionButtonProps) => {
    const t = useTranslations();
    const { onClose } = useUnderAgeVerificationModalContext();

    return (
        <Layout
            element={Button}
            data-testid="under-age-verification-modal-okay-button"
            onClick={(event) => {
                onClick?.(event);
                onClose();
            }}
            {...props}
        >
            {t('WORDS.OK')}
        </Layout>
    );
};

export { UnderAgeVerificationModalWrapper as UnderAgeVerificationModal };

export {
    UnderAgeVerificationModalWrapper as Wrapper,
    UnderAgeVerificationModalProvider as Provider,
    UnderAgeVerificationModalTitle as Title,
    UnderAgeVerificationModalDescription as Description,
    UnderAgeVerificationModalOkayButton as OkayButton,
};
